// @ts-nocheck
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore/lite";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
  uploadString,
} from "firebase/storage";
import { useFormik } from "formik";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";
import type React from "react";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DrawingFeature,
  Geocoder,
  InfoToast,
  Map,
} from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import {
  convertColorToHSL,
  generateThemeColor,
  hex2hsl,
} from "senf-atomic-design-system/src/styles/helpers";
import {
  db,
  delay,
  generateFirebaseId,
  useAuthContext,
  useGlobalState,
} from "senf-shared";
import type { Project } from "senf-shared/types";
import { fetchGisDataFromStorage } from "senf-shared/utils/mapHelpers";
import {
  captureMapScreenshot,
  resizeBase64Image,
} from "senf-shared/utils/mapScreenshot";
import { v4 as uuidv4 } from "uuid";
import { flyToPolygon } from "../utils/flyToPolygon";
import CesiumControls from "./components/CesiumControls";
import { flyToMapbounds, zoomToAssetById } from "./components/cesium/cesium";
import GisCreateBuilder from "./components/GisCreateBuilder";
import GisUploadBuilder from "./components/GisUploadBuilder";
import Header from "./components/Header";
import PlanningareaBuilder from "./components/PlanningareaBuilder";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface Props {
  mapId?: string;
  handleUpload: (id: string) => void;
  project?: Project;
  setProject: (project: Project) => Promise<void>;
}

interface MapBuilderFormValues {
  title?: string;
  mapId?: string;
  organizationId?: string;
  projectId?: string;
  gisData?: Array<{
    uid: string;
    title: string;
    layerTitle: string;
    layerName: string;
    layerType: string;
    icon: string;
    baseUrl: string;
    data: any;
    type: string;
    legendAttributes: any[];
    selectedAttribute: any;
    selectedTooltip: string;
    withLegend: boolean;
  }>;
  mapbounds?: any;
  mapStyle?: string | null;
  bimModels?: Array<{
    assetId: number;
    assetName: string;
    detectedEpsg: string;
    fileSize: string;
    uploadDate: Date;
    styles?: StyleCondition[];
  }>;
  // Cesium tileset settings
  osmBuildingsEnabled?: boolean;
  osmBuildingColor?: string;
  osmBuildingOpacity?: number;
  imageryStyle?: string;
  geoidHeightDifference?: number | null;
  thumbnail?: string;
  createdAt?: string;
  updatedAt?: string;
}

const MapBuilder: FC<Props> = ({
  mapId,
  handleUpload,
  project,
  setProject,
}) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModals();
  const mutatableMode = useRef<"edit" | "create" | null>(null);
  const { user } = useAuthContext();
  const [statefulMap, setStatefulMap] = useState<any>(null);
  const [drawMode, setDrawMode] = useState<{
    mode: string | null;
    drawType: string | null;
  }>({ mode: null, drawType: null });
  const currentMapId = mapId || generateFirebaseId();
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [index, setIndex] = useState<string>("planningArea");
  const [customGisdataIndex, setCustomGisdataIndex] = useState("planningArea");
  const [changedStyle, setChangedStyle] = useState<any>(null);
  const [cesiumData, setCesiumData] = useState<any>(null);
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;
  const [isCesiumReady, setIsCesiumReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const initialColor = generateThemeColor(hex2hsl(project?.color || "")).color[
    "400"
  ];

  const formik = useFormik<MapBuilderFormValues>({
    initialValues: {
      title: `${t("global:map")}-${project?.maps?.length || 0 + 1 || 1}`,
      mapId: currentMapId,
      organizationId: project?.organizationId || "",
      projectId: project?.projectId || "",
      mapStyle: null,
      mapbounds: null,
      bimModels: [],
      // Cesium tileset settings - default to false
      osmBuildingsEnabled: false,
      osmBuildingColor: "#ffffff",
      osmBuildingOpacity: 1,
      imageryStyle: "mapbox-outdoors",
      geoidHeightDifference: null,
      gisData: [
        {
          uid: uuidv4(),
          title: "Custom",
          layerTitle: "Custom",
          layerName: "",
          layerType: "",
          icon: "",
          baseUrl: "",
          data: "",
          type: "geojson",
          legendAttributes: [],
          selectedAttribute: null,
          selectedTooltip: "tooltipLabel",
          withLegend: false,
        },
      ],
    },
    onSubmit: () => {
      // onSubmit is handled by handleSubmit function
    },
  });

  useEffect(() => {
    if (JSON.stringify(formik.values.gisData)) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [JSON.stringify(formik.values.gisData)]);
  useEffect(() => {
    if (formik.values.gisData[0]?.selectedAttribute) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [formik.values.gisData[0]?.selectedAttribute]);
  useEffect(() => {
    if (customGisdataIndex) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [customGisdataIndex]);

  useEffect(() => {
    if (formik.values.gisData[0]?.data === null) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [formik.values.gisData[0]?.data]);

  useEffect(() => {
    if (mapId) {
      const schemaRef = doc(
        db,
        `organizations/${project?.organizationId}/projects/${project?.projectId}/maps/${mapId}`,
      );
      const fetchMap = async () => {
        const docSnap = await getDoc(schemaRef);

        if (docSnap.exists()) {
          mutatableMode.current = "edit";

          const gisData = await fetchGisDataFromStorage(docSnap.data().gisData);

          formik.setValues({
            title: docSnap.data().title,
            mapbounds: JSON.parse(docSnap.data().mapbounds),
            mapStyle: docSnap.data()?.mapStyle || null,
            gisData,
            bimModels: docSnap.data().bimModels || [],
            // Load Cesium tileset settings with default fallbacks
            osmBuildingsEnabled: docSnap.data().osmBuildingsEnabled || false,
            osmBuildingColor: docSnap.data().osmBuildingColor || "#ffffff",
            osmBuildingOpacity: docSnap.data().osmBuildingOpacity ?? 1,
            imageryStyle: docSnap.data().imageryStyle || "mapbox-outdoors",
            thumbnail: docSnap.data().thumbnail || null,
            createdAt: docSnap.data().createdAt,
            geoidHeightDifference: docSnap.data().geoidHeightDifference ?? null,
          });

          setIndex("create");
          setTimeout(() => {
            setChangedStyle(false);
            setTimeout(() => {
              setChangedStyle(true);
            }, 10);
          }, 1000);
        } else {
          mutatableMode.current = "create";
        }
      };

      fetchMap();
    } else {
      mutatableMode.current = "create";
    }
  }, [mapId]);

  // Track if initial zoom has been performed to prevent unwanted auto-zoom during styling
  const initialZoomPerformed = useRef(false);

  useEffect(() => {
    if (
      isCesiumReady &&
      formik.values.mapStyle === "cesium" &&
      formik.values.bimModels &&
      formik.values.bimModels.length > 0 &&
      !initialZoomPerformed.current
    ) {
      // Only zoom on initial load, not when styling or other changes occur
      zoomToAssetById(formik.values.bimModels[0].assetId, 0);
      initialZoomPerformed.current = true;
    }
  }, [isCesiumReady, formik.values.mapStyle, formik.values.bimModels]);

  // Helper function to capture and upload map thumbnail
  const captureThumbnailAndUpload = async (): Promise<string | null> => {
    const isCesiumMap = formik.values.mapStyle === "cesium";

    try {
      if (
        (isCesiumMap && !statefulCesiumMap) ||
        (!isCesiumMap && !statefulMap)
      ) {
        console.warn("No map instance available for thumbnail capture");
        return null;
      }

      // Determine map type and get the appropriate map instance
      const mapInstance = isCesiumMap ? statefulCesiumMap.viewer : statefulMap;

      // Debug logging - check actual canvas dimensions
      const actualCanvas = !isCesiumMap ? statefulMap?.getCanvas() : null;
      const cesiumCanvas = isCesiumMap
        ? statefulCesiumMap?.viewer?.canvas
        : null;
      // Capture screenshot from the map using actual canvas dimensions
      let screenshotWidth = 800;
      let screenshotHeight = 600;

      // Use actual canvas dimensions to avoid squashing
      if (!isCesiumMap && actualCanvas) {
        screenshotWidth = actualCanvas.width;
        screenshotHeight = actualCanvas.height;
      } else if (isCesiumMap && cesiumCanvas) {
        screenshotWidth = cesiumCanvas.width;
        screenshotHeight = cesiumCanvas.height;
      }
      const screenshot = await captureMapScreenshot(mapInstance, {
        width: screenshotWidth,
        height: screenshotHeight,
        quality: 0.8,
        format: "jpeg",
      });

      if (!screenshot) {
        console.warn("Failed to capture map screenshot");
        return null;
      }

      // Resize to thumbnail size (larger for better quality)
      const thumbnailBase64 = await resizeBase64Image(
        screenshot,
        800,
        600,
        0.8,
      );

      if (!thumbnailBase64) {
        console.warn("Failed to resize screenshot");
        return null;
      }

      // Convert base64 to blob
      const base64Data = thumbnailBase64.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/jpeg" });

      // Upload to Firebase Storage
      const storage = getStorage();
      const thumbnailPath = `organizationsData/${project?.organizationId}/projects/${project?.projectId}/maps/${currentMapId}/thumbnail`;
      const thumbnailRef = storageRef(storage, thumbnailPath);

      await uploadBytes(thumbnailRef, blob, {
        customMetadata: {
          hidden: "true",
          hiddenAt: new Date().toISOString(),
        },
      });
      const downloadURL = await getDownloadURL(thumbnailRef);

      return downloadURL;
    } catch (error) {
      console.error("❌ Error capturing/uploading thumbnail:", error);
      return null;
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    // Fly to mapbounds based on map style
    if (formik.values.mapStyle === "cesium") {
      // Use Cesium flyToMapbounds
      flyToMapbounds(formik.values.mapbounds);
    } else if (statefulMap) {
      // Use Mapbox flyToPolygon
      flyToPolygon(statefulMap, formik.values.mapbounds, {
        duration: 2000,
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
      });
    }

    await delay(2000);

    try {
      const gisDataString = JSON.stringify(formik.values.gisData);
      const gisDataSize = new Blob([gisDataString]).size;
      let gisDataField = gisDataString;

      if (gisDataSize > 500 * 1024) {
        // 500KB in bytes - upload to Firebase Storage
        try {
          const storage = getStorage();
          const path = `organizationsData/${project.organizationId}/projects/${project.projectId}/maps/${currentMapId}/gisData/gisData.json`;
          const ref = storageRef(storage, path);
          await uploadString(ref, gisDataString);
          const downloadURL = await getDownloadURL(ref);
          gisDataField = downloadURL;
        } catch (error) {
          console.error("Error uploading gisData to Firebase Storage:", error);
          console.error("Error details:", {
            code: error.code,
            message: error.message,
            gisDataSize: gisDataSize,
            path: `organizationsData/${project.organizationId}/projects/${project.projectId}/maps/${currentMapId}/gisData/gisData.json`,
          });
          alert(
            `Failed to upload large gisData. Error: ${error.message || "Unknown error"}. Please try again.`,
          );
          return;
        }
      }

      // Capture and upload thumbnail
      let thumbnailUrl = formik.values.thumbnail; // Keep existing thumbnail if any
      try {
        const capturedThumbnailUrl = await captureThumbnailAndUpload();
        if (capturedThumbnailUrl) {
          thumbnailUrl = capturedThumbnailUrl;
        }
      } catch (thumbnailError) {
        console.warn(
          "⚠️ Failed to capture thumbnail, continuing without it:",
          thumbnailError,
        );
        // Continue with save even if thumbnail fails - it's not critical
      }

      // const filteredGisData = formik.values.gisData
      //   // .slice(1) // This skips the first gisData object
      //   .map((dataItem) => {
      //     const { layers, ...rest } = dataItem;
      //     return rest;
      //   });
      const savedData = {
        organizationId: project?.organizationId,
        projectId: project?.projectId,
        mapId: currentMapId,
        title: formik.values.title,
        mapStyle: formik.values.mapStyle,
        mapbounds: JSON.stringify(formik.values.mapbounds),
        gisData: gisDataField,
        bimModels: formik.values.bimModels || [],
        // Save Cesium tileset settings
        osmBuildingsEnabled: formik.values.osmBuildingsEnabled || false,
        osmBuildingColor: formik.values.osmBuildingColor,
        osmBuildingOpacity: formik.values.osmBuildingOpacity,
        imageryStyle: formik.values.imageryStyle || "mapbox-outdoors",
        geoidHeightDifference: formik.values.geoidHeightDifference,
        thumbnail: thumbnailUrl, // Add thumbnail URL to saved data
        createdAt:
          mutatableMode.current === "create"
            ? new Date().toISOString()
            : formik.values.createdAt,
        updatedAt: new Date().toISOString(),
      };

      // flyToPolygon(statefulMap, formik.values.mapbounds, { duration: 1000 });

      await setDoc(
        doc(
          collection(
            db,
            `organizations/${project?.organizationId}/projects/${project?.projectId}/maps`,
          ),
          currentMapId,
        ),
        savedData,
      );

      setProject(project?.projectId as any).then(() => {
        closeModal();
        setTimeout(() => {
          handleUpload(currentMapId);
        }, 500);
      });
    } catch (error: unknown) {
      console.error("❌ Error saving map:", {
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined,
        mapId: currentMapId,
        title: formik.values.title,
        organizationId: project?.organizationId,
        projectId: project?.projectId,
        timestamp: new Date().toISOString(),
        formValues: {
          title: formik.values.title,
          mapStyle: formik.values.mapStyle,
          hasMapbounds: !!formik.values.mapbounds,
          gisDataLength: formik.values.gisData?.length || 0,
          bimModelsLength: formik.values.bimModels?.length || 0,
          imageryStyle: formik.values.imageryStyle,
        },
      });

      // Show user-friendly error message
      alert(
        t("global:errorSavingMap") || "Error saving map. Please try again.",
      );

      throw error;
    } finally {
      setTimeout(() => {
        setIsSaving(false);
      }, 3000);
    }
  };

  const handleSaveDrawn = (polygon: FeatureCollection) => {
    if (index === "planningArea") {
      if (polygon.features.length < 1) {
        formik.setFieldValue("mapbounds", null);
      } else {
        formik.setFieldValue("mapbounds", polygon);
      }
    } else if (polygon && polygon.features && polygon.features.length > 0) {
      const validFeatures = polygon.features.filter(
        (feature: Feature<Geometry, GeoJsonProperties>) =>
          (feature.geometry as any).coordinates &&
          (feature.geometry as any).coordinates.length > 0,
      );

      let number = 1; // Initialize a counter for the number property

      const updatedFeatures = validFeatures.map(
        (feature: Feature<Geometry, GeoJsonProperties>, index) => {
          const newProperties: any = {
            ...feature.properties,
            // Set datasetIndex only if it's not defined

            datasetIndex: feature.properties?.datasetIndex || 0,
            color: feature.properties?.color || initialColor,
            labelColor: generateThemeColor(
              convertColorToHSL(
                feature.properties?.color || initialColor || [0, 0, 0],
              ) || [0, 0, 0],
            )?.color["800"],
          };

          // Add number property only if datasetIndex was not defined
          if (newProperties.datasetIndex === 0) {
            newProperties.number = number++;
          }

          return {
            ...feature,
            properties: newProperties,
          };
        },
      );

      [...new Array(formik.values.gisData?.length)].map((data, index) => {
        const updatedGeoData = {
          type: "FeatureCollection",
          features: updatedFeatures.filter(
            (feature: Feature<Geometry, GeoJsonProperties>) =>
              feature.properties?.datasetIndex === index,
          ),
        };
        formik.setFieldValue(`gisData[${index}].data`, updatedGeoData);
      });
    }
    setDrawMode({ mode: "draw", drawType: null });
  };

  // useEffect(() => {
  //   setDrawMode({ mode: "draw", drawType: null });
  //   if (statefulMap && statefulMap.df && statefulMap.df) {
  //     console.log(statefulMap.df);
  //     setTimeout(() => {
  //       statefulMap.df.deleteAll();
  //     }, 100);
  //   }
  // }, [index, statefulMap?.df]);

  const handleDelete = async () => {
    const mapRef = doc(
      db,
      `organizations/${project?.organizationId}/projects/${project?.projectId}/maps/${mapId}`,
    );
    await deleteDoc(mapRef);

    setProject(project?.projectId as any).then(() => {
      closeModal();
    });
  };

  const [featureCollection, setFeatureCollection] =
    useState<FeatureCollection | null>(null);

  useEffect(() => {
    if (index === "planningArea") {
      // If index is 0, simply take the mapbounds
      setFeatureCollection(formik.values.mapbounds);
    } else if (index === "create" || index === "upload") {
      setFeatureCollection(null);

      const gisData = formik.values.gisData || [];
      const feat = gisData
        // Filter out any items where data is null or undefined
        .filter((gisItem) => gisItem?.data?.features?.length)
        // Map over the filtered items to extract the features
        .flatMap((gisItem) => gisItem?.data?.features || []); // Flatten the array of features arrays

      // Now feat is a single array containing all features from all the feature collections
      const featureCollectionObject = {
        type: "FeatureCollection",
        features: feat,
      };

      // Set the feature collection if it's not empty
      if (featureCollectionObject.features.length > 0) {
        setFeatureCollection(featureCollectionObject as any);
      }
    }
  }, [
    index,
    JSON.stringify(formik.values.gisData),
    JSON.stringify(formik.values.mapbounds),
  ]);

  const mapStyles = [
    {
      label: "Default Map Style",
      value: null,
      onClick: () => formik.setFieldValue("mapStyle", null),
    },
    {
      label: "Default Map Style (New)",
      value: "mapbox://styles/tmorino/cmg9hrhi6002s01s5835aftbe",
      onClick: () =>
        formik.setFieldValue(
          "mapStyle",
          "mapbox://styles/tmorino/cmg9hrhi6002s01s5835aftbe",
        ),
    },
    {
      label: "White map",
      value: "mapbox://styles/tmorino/cltfrpygw008n01nr0gnhbis7",
      onClick: () =>
        formik.setFieldValue(
          "mapStyle",
          "mapbox://styles/tmorino/cltfrpygw008n01nr0gnhbis7",
        ),
    },

    {
      label: "Cesium",
      value: "cesium",
      onClick: () => formik.setFieldValue("mapStyle", "cesium"),
    },
    // {
    //   text: "Cesium with Rendering",
    //   value: "cesium-rendering",
    //   onClick: () => formik.setFieldValue("mapStyle", "cesium-rendering"),
    // },
  ]?.filter(Boolean);

  const deleteFeature = async (featureId: string) => {
    const featureCollection: FeatureCollection | undefined =
      formik.values.gisData?.[0]?.data;

    if (
      featureCollection &&
      featureCollection.features &&
      featureCollection.features.length > 0
    ) {
      // Filter out the feature with the given ID
      const filteredFeatures = featureCollection.features.filter(
        (feature) => feature.id !== featureId,
      );

      // Return a new FeatureCollection without the deleted feature
      const newFeatureCollection = {
        ...featureCollection,
        features: filteredFeatures,
      };
      formik.setFieldValue(`gisData[${0}].data`, newFeatureCollection);
    }
  };

  const [cameraPosition, setCameraPosition] = useState<{
    center: [number, number];
    height: number;
  } | null>(null);

  const handleCameraMove = (position: {
    center: [number, number];
    height: number;
  }) => {
    setCameraPosition(position);
  };

  const handleDrawComplete = (featureCollection: GeoJSON.FeatureCollection) => {
    // Update the GIS data with the new feature collection
    const newGisData = {
      ...formik.values.gisData?.[0],
      data: featureCollection,
      layerName:
        drawMode.drawType === "polygon" ? "Drawn Polygon" : "Drawn Line",
      layerType: drawMode.drawType === "polygon" ? "polygon" : "line",
    };

    formik.setFieldValue("gisData", [newGisData]);
  };

  const isDisabled = useMemo(() => {
    if (
      formik.values.mapStyle === "cesium" ||
      formik.values.mapStyle === "cesium-rendering"
    ) {
      return undefined;
    }
    if (!formik.values.title) return t("global:chooseTitle");
    if (
      featureId ||
      !formik.values.mapbounds ||
      !formik.values.mapbounds?.features?.length
    ) {
      return t("global:enterAllRequiredData");
    }
    return undefined;
  }, [
    formik.values.title,
    featureId,
    formik.values.mapbounds,
    formik.values.mapStyle,
    t,
  ]);

  useEffect(() => {
    if (formik.values.mapbounds && statefulMap) {
      flyToPolygon(statefulMap, formik.values.mapbounds, { duration: 2000 });
    }
  }, [formik.values.mapbounds, statefulMap]);

  return (
    <div
      className="flex h-screen w-screen"
      style={{ "--sidebar-width": "545px" } as React.CSSProperties}
    >
      <div className="relative h-screen w-[calc(100vw-var(--sidebar-width))]">
        <div className="l-0 absolute top-0 z-10 m-3 w-96">
          <Geocoder
            isCesium={formik.values.mapStyle === "cesium"}
            statefulMap={statefulMap}
            statefulCesiumMap={statefulCesiumMap}
            geolocate={false}
            placeholder={t("util:search", { val: t("global:place") })}
            classNames={{
              inputContainer: "bg-surface-default",
            }}
          />
        </div>
        <div className="right-[570px] top-2.5 w-[350px] z-50 fixed">
          {index === "planningArea" &&
          !formik.values.mapbounds?.features?.length ? (
            <InfoToast
              text={t("map:builder.planningArea.infoText")}
              className="w-full"
              closeable={true}
              withShadow={true}
            />
          ) : null}
          {index === "upload" &&
          (!formik.values.gisData?.length > 1 ||
            !formik.values.bimModels?.length) ? (
            <InfoToast
              text={t("map:builder.gisUpload.infoText")}
              className="w-full"
              closeable={true}
              withShadow={true}
            />
          ) : null}
          {index === "create" && (
            <InfoToast
              text={t("map:builder.gisCreate.infoText")}
              className="w-full"
              closeable={true}
              withShadow={true}
            />
          )}
        </div>

        <Map
          cesiumCameraPosition={cameraPosition}
          handleCesiumCameraMove={handleCameraMove}
          statefulMap={statefulMap}
          setStatefulMap={setStatefulMap}
          flyToMapbounds={false}
          mapbounds={formik.values.mapbounds ? formik.values.mapbounds : null}
          mapStyle={formik.values.mapStyle}
          optimizeString={true}
          preserveDrawingBuffer={true}
          legendData={formik.values.gisData}
          // bimAssetIds={
          //   formik.values.bimModels?.map((model) => model.assetId) || []
          // }
          // Pass Cesium imagery settings from formik
          imageryStyle={formik.values.imageryStyle || "mapbox-outdoors"}
          onDrawComplete={handleDrawComplete}
          onCesiumReady={() => setIsCesiumReady(true)}
          enableBimHoverHighlight
          mapData={{
            geoidHeightDifference: formik.values.geoidHeightDifference,
            osmBuildingsEnabled: formik.values.osmBuildingsEnabled,
            osmBuildingColor: formik.values.osmBuildingColor,
            osmBuildingOpacity: formik.values.osmBuildingOpacity,
            bimModels: formik.values.bimModels || [],
          }}
          enableCameraBoundsRestriction={false}
          // gisData={
          //   index === 1 &&
          //   customGisdataIndex !== "data" &&
          //   formik.values.gisData?.[0] &&
          //   changedStyle
          //     ? formik.values.gisData?.[0]
          //     : false
          // }
        />

        <div className="bottom-2 start-0 h-20 fixed z-50 flex items-center justify-center w-[calc(100vw-var(--sidebar-width))]">
          <DrawingFeature
            mapStyle={formik.values.mapStyle}
            mapType={drawMode}
            statefulMap={statefulMap}
            handleSaveDrawn={handleSaveDrawn as any}
            featureCollection={featureCollection}
            bottom={"100px"}
            featureId={featureId}
            setFeatureId={setFeatureId}
            deleteFeature={deleteFeature}
            openDescription={false}
            // maxFeatures={1}
            shouldTriggerRefresh={[changedStyle, index].toString()}
            // flyToGeoData={formik.values?.mapbounds}
            setDrawMode={setDrawMode}
            allowClickMapToStartDrawing={false}
            // initialDrawType={index === "planningArea" && "mapbounds"}
          />
        </div>
      </div>

      <div className="border-border-subdued z-50 flex h-screen w-[var(--sidebar-width)] min-w-[var(--sidebar-width)] flex-col border-l">
        <Header
          title={formik.values.title}
          setTitle={(e) => formik.setFieldValue("title", e.target.value)}
          handleDelete={handleDelete}
          index={index}
          setIndex={setIndex}
          formik={formik}
          mapStyles={mapStyles}
        />

        {/* {formik.values.mapStyle === "cesium" && (
          <div className="border-border-subdued border-t p-4">
            <h3 className="text-lg font-semibold mb-2">Cesium Map Data</h3>
            <div className="space-y-2">
            {cameraPosition && (
        <div className="">
          <h3 className="font-bold mb-2">Camera Position</h3>
          <p>Center: [{cameraPosition.center[0].toFixed(6)}°, {cameraPosition.center[1].toFixed(6)}°]</p>
          <p>Height: {Math.round(cameraPosition.height)}m</p>
        </div>
      )}

 <Button onClick={() => {
  if (cameraPosition) {
    // Add some random variation to the current position
    const newPosition = {
      center: [
        cameraPosition.center[0] + (Math.random() - 0.5) * 0.1,
        cameraPosition.center[1] + (Math.random() - 0.5) * 0.1
      ] as [number, number],
      height: cameraPosition.height * (0.8 + Math.random() * 0.4) // Random height between 80% and 120% of current
    };
    handleCameraMove(newPosition);
  }
}}
text="Change camera position"
/>  
                 
            
            </div>
          </div>
        )} */}

        <div className="h-full overflow-y-scroll ">
          <div className="flex h-full flex-col gap-3 p-8">
            {index === "planningArea" && (
              <PlanningareaBuilder
                formik={formik}
                setDrawMode={setDrawMode}
                drawMode={drawMode}
                featureId={featureId}
                statefulMap={statefulMap}
                mapStyles={mapStyles}
                project={project}
              />
            )}
            {index === "upload" && (
              <GisUploadBuilder
                formik={formik}
                setDrawMode={setDrawMode}
                initialColor={initialColor}
              />
            )}
            {index === "create" && (
              <GisCreateBuilder
                formik={formik}
                setDrawMode={setDrawMode}
                drawMode={drawMode}
                featureId={featureId}
                handleSaveDrawn={handleSaveDrawn}
                deleteFeature={deleteFeature}
                initialColor={initialColor}
              />
            )}
          </div>
        </div>
        <div className="border-border-subdued relative  bottom-0 right-0 flex w-full justify-end gap-2 border-t bg-white p-4">
          {/* {index > 0 && (
            <Button
              size="lg"
              onClick={() => setIndex((prev) => prev - 1)}
              leadingIcon="arrowhead-left-md"
            />
          )} */}

          <Button
            id="submitButton"
            variant="primary"
            size="lg"
            disabled={!!isDisabled || isSaving}
            disabledNotice={isDisabled}
            loading={isSaving}
            onClick={() => {
              if (mutatableMode.current === "create") {
                if (index === "planningArea") {
                  setIndex("upload");
                  return;
                }
                if (index === "upload") {
                  setIndex("create");
                  return;
                }
              }
              if (mutatableMode.current === "edit" || index === "create") {
                handleSubmit();
              }
            }}
            text={
              isSaving
                ? t("global:save")
                : mutatableMode.current === "create" && index !== "create"
                  ? t("global:next")
                  : t("global:save")
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MapBuilder;
